import { FC } from "react";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
} from "../../../../../../../../components";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import {
  convertToUserFormat,
  extractValue,
  getValueOrBlank,
  isEmpty,
} from "../../../../../../../../helpers/utils";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import {
  OccupationalLevelOtherKey,
  OccupationalFunctionOtherKey,
  ParticipantDetail,
} from "../../../../../../../createProgram/modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../../../createProgram/siteCore/SiteCoreKeys";
import { useGridContainer } from "../../../../../../../../hooks/useGridContainer";
import { SITE_CORE_COUNTRIES_VALUE } from "../../../../../../../../helpers/constants";

interface SponsoringEmployerProp {
  participantDetail?: ParticipantDetail;
  deliveryCountry?: string;
}
export const SponsoringEmployer: FC<SponsoringEmployerProp> = ({
  participantDetail,
  deliveryCountry = "",
}) => {
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();
  const { fetchLabel, fetchValueByKeyOfDropdown } = useSiteCore();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const getLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.sponsoringEmployer, key);
  };

  const getValue = (value?: string | number) => {
    return isEmpty(value) ? "--" : value;
  };

  const formatDate = (date?: string): string => {
    return date ? convertToUserFormat(date, userCountryDateFormat) : "";
  };

  const isOccupationLevelOtherVisible =
    participantDetail?.occupationalLevel === OccupationalLevelOtherKey;
  const isOccupationFunctionOtherVisible =
    participantDetail?.occupationalFunction === OccupationalFunctionOtherKey;

  return (
    <MuiBox>
      <MuiGrid
        container
        data-testid="sponsoring-employer"
        className="content sponsoring-employer-grid"
      >
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.clientName)}
            value={getValue(participantDetail?.companyName)}
            isValueWithEllipsis
          />
        </MuiGrid>
      </MuiGrid>
      <MuiGrid container className="content">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.jobTitle)}
            value={getValue(participantDetail?.jobTitle)}
            isValueWithEllipsis
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.employeeId)}
            value={getValue(participantDetail?.employeeId)}
            isValueWithEllipsis
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.employeeBand)}
            value={getValue(participantDetail?.employeeBand)}
            isValueWithEllipsis
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.totalYearsEmployed)}
            value={getValue(participantDetail?.totalYearsEmployed)}
            isValueWithEllipsis
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.currentEmploymentStartDate)}
            value={getValue(formatDate(participantDetail?.currentEmploymentStartDate))}
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.occupationalFunction)}
            value={getValue(
              extractValue(participantDetail?.occupationalFunction),
            )}
            isValueWithEllipsis
          />
        </MuiGrid>
        { isOccupationFunctionOtherVisible && ( 
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(programSiteCoreKeys.occupationalFunctionOther)}
              value={getValue(participantDetail?.occupationalFunctionOther)}
              isValueWithEllipsis
            />
          </MuiGrid>
        )}
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.occupationalLevel)}
            value={getValue(extractValue(participantDetail?.occupationalLevel))}
            isValueWithEllipsis
          />
        </MuiGrid>
        {isOccupationLevelOtherVisible && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(programSiteCoreKeys.occupationalLevelOther)}
              value={getValue(participantDetail?.occupationalLevelOther)}
              isValueWithEllipsis
            />
          </MuiGrid>
        )}
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.planPostedDate)}
            value={getValue(formatDate(participantDetail?.planPostedDate))}
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.bestMatchPreference)}
            value={getValue(participantDetail?.bestMatchPreference)}
            isValueWithEllipsis
          />
        </MuiGrid>

        {deliveryCountry === SITE_CORE_COUNTRIES_VALUE.COUNTRY_BELGIUM && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(programSiteCoreKeys.participantGroupType)}
              value={fetchValueByKeyOfDropdown(
                programSiteCoreKeys.sponsoringEmployer,
                programSiteCoreKeys.participantGroupTypeList,
                getValueOrBlank(participantDetail?.participantGroupType),
              )}
              replaceEmptyWithDash
              isValueWithEllipsis
            />
          </MuiGrid>
        )}

        {deliveryCountry === SITE_CORE_COUNTRIES_VALUE.COUNTRY_BELGIUM && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(programSiteCoreKeys.socialFund)}
              value={fetchValueByKeyOfDropdown(
                programSiteCoreKeys.sponsoringEmployer,
                programSiteCoreKeys.socialFundList,
                getValueOrBlank(participantDetail?.socialFund),
              )}
              isValueWithEllipsis
              replaceEmptyWithDash
            />
          </MuiGrid>
        )}

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(programSiteCoreKeys.datePlanApproved)}
            value={getValue(formatDate(participantDetail?.datePlanApproved))}
          />
        </MuiGrid>
      </MuiGrid>
    </MuiBox>
  );
};
